import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "~/components/ui/use-toast";
import type { TSession } from "~/database/types"; // Assuming TSession is the type for a session and has uuid and needsPractitionerReview
import { useApiClient } from "~/hooks/use-api-client";
import { captureException } from "~/lib/analytics";
import { logger } from "~/lib/logger";

// Define SessionApiResponse locally to match the structure in use-session.ts
interface SessionApiResponse {
    session: TSession;
    parts: unknown; // Actual type TPart[] from use-session, but unknown is fine if parts aren't modified here
    effectiveSettings?: unknown; // Actual type TClientSettings from use-session
}

interface UpdateSessionReviewStatusPayload {
    sessionId: string;
    needsReview: boolean;
}

// Define a type for the context passed between onMutate, onError, and onSettled
interface MutationContext {
    previousPractitionerSessionsAll?: TSession[];
    previousPractitionerSessionsNeedsReview?: TSession[];
    // This will store the data for the ["session", sessionId, null] query key
    previousSessionDataForNullToken?: SessionApiResponse;
}

export function useUpdateSessionReviewStatus() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation<
        unknown, // Type of data returned by mutationFn (e.g., { success: true })
        Error, // Type of error
        UpdateSessionReviewStatusPayload, // Type of variables passed to mutationFn
        MutationContext // Type of context
    >({
        mutationFn: async (payload: UpdateSessionReviewStatusPayload) => {
            const { sessionId, needsReview } = payload;
            logger
                .withMetadata({ sessionId, needsReview })
                .info("Updating session review status");

            const response = await apiClient.post("/session/mutations", {
                action: "updateNeedsPractitionerReview",
                sessionId: sessionId,
                needsReview: needsReview,
            });

            if (!response.ok) {
                const errorMessage = `Failed to update session review status: ${response.status} ${response.statusText}`;
                logger
                    .withMetadata({
                        sessionId,
                        needsReview,
                        status: response.status,
                    })
                    .error(errorMessage);

                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || errorMessage);
                } catch (e) {
                    throw new Error(errorMessage);
                }
            }
            // Assuming the backend returns { success: true } or similar
            return response.json();
        },
        onMutate: async (newData: UpdateSessionReviewStatusPayload) => {
            const { sessionId, needsReview } = newData;
            logger
                .withMetadata({ sessionId, needsReview })
                .info("Optimistically updating session review status");

            await queryClient.cancelQueries({
                queryKey: ["practitioner", "sessions", false],
            });
            await queryClient.cancelQueries({
                queryKey: ["practitioner", "sessions", true],
            });
            await queryClient.cancelQueries({
                queryKey: ["session", sessionId],
            });

            const previousPractitionerSessionsAll = queryClient.getQueryData<
                TSession[]
            >(["practitioner", "sessions", false]);
            const previousPractitionerSessionsNeedsReview =
                queryClient.getQueryData<TSession[]>([
                    "practitioner",
                    "sessions",
                    true,
                ]);

            if (previousPractitionerSessionsAll) {
                const updatedAllSessions = previousPractitionerSessionsAll.map(
                    (session) =>
                        session.uuid === sessionId
                            ? {
                                  ...session,
                                  needsPractitionerReview: needsReview,
                              }
                            : session,
                );
                queryClient.setQueryData(
                    ["practitioner", "sessions", false],
                    updatedAllSessions,
                );

                const updatedNeedsReviewSessions = updatedAllSessions.filter(
                    (session) => session.needsPractitionerReview,
                );
                queryClient.setQueryData(
                    ["practitioner", "sessions", true],
                    updatedNeedsReviewSessions,
                );
            }

            // Optimistic update for useSession cache, targeting ["session", sessionId, null]
            const sessionQueryKeyForNullToken = ["session", sessionId, null];
            const previousSessionDataForNullToken =
                queryClient.getQueryData<SessionApiResponse>(
                    sessionQueryKeyForNullToken,
                );

            if (previousSessionDataForNullToken?.session) {
                const updatedSessionApiResponse: SessionApiResponse = {
                    ...previousSessionDataForNullToken,
                    session: {
                        ...previousSessionDataForNullToken.session,
                        needsPractitionerReview: needsReview,
                    },
                };
                queryClient.setQueryData<SessionApiResponse>(
                    sessionQueryKeyForNullToken,
                    updatedSessionApiResponse,
                );
            }

            return {
                previousPractitionerSessionsAll,
                previousPractitionerSessionsNeedsReview,
                previousSessionDataForNullToken,
            };
        },
        onError: (
            err: Error,
            newData: UpdateSessionReviewStatusPayload,
            context?: MutationContext,
        ) => {
            logger
                .withError(err)
                .withMetadata({
                    sessionId: newData.sessionId,
                    needsReview: newData.needsReview,
                })
                .error("Optimistic update failed, rolling back");

            if (context?.previousPractitionerSessionsAll) {
                queryClient.setQueryData(
                    ["practitioner", "sessions", false],
                    context.previousPractitionerSessionsAll,
                );
            }
            if (context?.previousPractitionerSessionsNeedsReview) {
                queryClient.setQueryData(
                    ["practitioner", "sessions", true],
                    context.previousPractitionerSessionsNeedsReview,
                );
            }

            // Rollback useSession cache for ["session", sessionId, null]
            if (context?.previousSessionDataForNullToken) {
                queryClient.setQueryData(
                    ["session", newData.sessionId, null], // Use the specific key for rollback
                    context.previousSessionDataForNullToken,
                );
            }

            captureException("Failed to update session review status", err);
            toast({
                variant: "destructive",
                title: "Error",
                description:
                    "Failed to update the session's review status. Please try again later.",
            });
        },
        onSuccess: (
            data: unknown,
            variables: UpdateSessionReviewStatusPayload,
        ) => {
            toast({
                title: "Session review status updated",
                description:
                    "The session's review status has been successfully updated.",
            });
        },
        onSettled: (
            data: unknown | undefined,
            error: Error | null,
            variables: UpdateSessionReviewStatusPayload,
            context?: MutationContext,
        ) => {
            const { sessionId } = variables;
            logger
                .withMetadata({ sessionId })
                .info(
                    "Invalidating queries after session review status update attempt",
                );
            queryClient.invalidateQueries({
                queryKey: ["practitioner", "sessions", false],
            });
            queryClient.invalidateQueries({
                queryKey: ["practitioner", "sessions", true],
            });
            queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
            queryClient.invalidateQueries({
                queryKey: ["practitioner", "clients"],
            });
        },
    });
}
